import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import EditFreelancerInfo from './EditFreelancerInfo';
import { useProfileStore } from '../../stores/profileStore';
import EditProfileSecurity from './EditProfileSecurity';
import EditCompanyInfo from './EditRecruiterInfo';
import { ROLES } from '../../types/auth';
import EditPublicProfile from './EditPublicProfile';
import EditRecruiterInfo from './EditRecruiterInfo';

interface ProfileSettingsSectionProps {
  isOnboarding?: boolean;
  onComplete?: () => void;
}

export const ProfileSettingsSection: React.FC<ProfileSettingsSectionProps> = ({
  isOnboarding = false,
  onComplete,
}) => {
  const { t } = useTranslation('settings');
  const { profile } = useProfileStore();

  const TABS = useMemo(
    () =>
      profile?.role === ROLES.FREELANCER
        ? [
            { key: 'publicProfile', label: t('tabs.publicProfile') },
            { key: 'myInfo', label: t('tabs.myInfo') },
            { key: 'security', label: t('tabs.security') },
          ]
        : [
            { key: 'myInfo', label: t('tabs.myInfo') },
            { key: 'security', label: t('tabs.security') },
          ],
    [profile?.role, t]
  );

  const getInitialTab = () => {
    if (isOnboarding && TABS.length > 0) {
      return TABS[0].key;
    }
    return profile?.role === ROLES.FREELANCER ? 'publicProfile' : 'myInfo';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);

  useEffect(() => {
    if (!isOnboarding) {
      if (profile?.role === ROLES.FREELANCER) {
        setActiveTab('publicProfile');
      } else {
        setActiveTab('myInfo');
      }
    }
  }, [profile?.role, isOnboarding]);

  useEffect(() => {
    if (isOnboarding && TABS.length > 0) {
      const currentTabExists = TABS.find(tab => tab.key === activeTab);
      if (!currentTabExists) {
        setActiveTab(TABS[0].key);
      }
    }
  }, [isOnboarding, TABS, activeTab]);

  const currentTabIndex = TABS.findIndex(tab => tab.key === activeTab);
  const isLastTab = currentTabIndex === TABS.length - 1;
  const isFirstTab = currentTabIndex === 0;

  const handlePrevious = () => {
    if (!isFirstTab) {
      const previousTab = TABS[currentTabIndex - 1];
      if (previousTab) {
        setActiveTab(previousTab.key);
      }
    }
  };

  const handleNext = () => {
    if (isLastTab) {
      onComplete?.();
    } else {
      const nextTab = TABS[currentTabIndex + 1];
      if (nextTab) {
        setActiveTab(nextTab.key);
      }
    }
  };

  const handleSkip = () => {
    if (!isLastTab) {
      const nextTab = TABS[currentTabIndex + 1];
      if (nextTab) {
        setActiveTab(nextTab.key);
      }
    }
  };

  const handleSave = () => {
    // For last tab, onSave should also complete onboarding
    if (isLastTab) {
      onComplete?.();
    }
  };

  const EditProfileInfo =
    profile?.role === ROLES.FREELANCER ? EditFreelancerInfo : EditRecruiterInfo;

  // --- Render ---
  return (
    <div className="w-full bg-white rounded-xl shadow p-4 md:p-10 lg:max-w-screen-lg lg:mx-auto">
      {/* Section Title */}
      <h2 className="text-secondary font-ambit text-[1.5625rem] font-black leading-[1.875rem] mb-6">
        {t('tabs.settings')}
      </h2>
      {/* Tabs: Dropdown on mobile, horizontal on desktop */}
      <div className="mb-6">
        {/* Mobile Dropdown */}
        <div className="md:hidden">
          <select
            data-testid="profile-tabs-select"
            className="w-full border border-[#C7D0D9] rounded-lg px-4 py-3 font-ambit text-[0.9375rem] leading-[1.25rem] text-secondary font-normal bg-white appearance-none focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
            value={activeTab}
            onChange={e => !isOnboarding && setActiveTab(e.target.value)}
            disabled={isOnboarding}
          >
            {TABS.map(tab => (
              <option
                key={tab.key}
                value={tab.key}
                className="font-ambit text-[0.9375rem] leading-[1.25rem] text-secondary font-bold"
              >
                {tab.label}
              </option>
            ))}
          </select>
        </div>
        {/* Desktop Tabs */}
        <div className="hidden md:flex">
          {TABS.map(tab => (
            <button
              key={tab.key}
              className={`py-3 px-6 font-ambit text-[0.9375rem] leading-[1.25rem] transition-colors duration-150 ${
                activeTab === tab.key
                  ? 'font-black text-white bg-primary'
                  : 'first:pl-0 font-normal text-primary'
              } ${isOnboarding ? '' : 'cursor-pointer'}`}
              onClick={() => !isOnboarding && setActiveTab(tab.key)}
              disabled={isOnboarding}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        {profile?.role === ROLES.FREELANCER && activeTab === 'publicProfile' && profile && (
          <EditPublicProfile
            profile={profile}
            onNext={isOnboarding && !isLastTab ? handleNext : undefined}
            onPrevious={isOnboarding && !isFirstTab ? handlePrevious : undefined}
            onSkip={isOnboarding && !isLastTab ? handleSkip : undefined}
            onSave={isOnboarding && isLastTab ? handleSave : undefined}
          />
        )}
        {activeTab === 'security' && (
          <EditProfileSecurity
            isOnboarding={isOnboarding}
            onNext={isOnboarding && !isLastTab ? handleNext : undefined}
            onPrevious={isOnboarding && !isFirstTab ? handlePrevious : undefined}
            onSkip={isOnboarding && !isLastTab ? handleSkip : undefined}
            onSave={isOnboarding && isLastTab ? handleSave : undefined}
          />
        )}
        {activeTab === 'company' && <EditCompanyInfo />}
        {activeTab === 'myInfo' && profile && (
          <EditProfileInfo
            profile={profile}
            onNext={isOnboarding && !isLastTab ? handleNext : undefined}
            onPrevious={isOnboarding && !isFirstTab ? handlePrevious : undefined}
            onSkip={isOnboarding && !isLastTab ? handleSkip : undefined}
            onSave={isOnboarding && isLastTab ? handleSave : undefined}
          />
        )}
      </div>
    </div>
  );
};
